import { browser } from '$app/env';
import { PUBLIC_WS_BASE_URL } from '$app/env/public';
import { generateUUID } from '#lib/helper/uuid.js';
import {
	AppEvent,
	type AppEventPayloadMap,
	type SocketMessage
} from '#lib/types/generated/index.js';
import { apiSocketToken } from '#lib/types/generated/api.gen.js';
import { DateTime } from 'luxon';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { get, writable } from 'svelte/store';
import { refreshAppData } from './app';
import { now } from './time';

const url = PUBLIC_WS_BASE_URL;
const numberMessagesToKeep = 50;

const debug: boolean = false;

let isConnecting = false;
let hasBeenConnected = false;
let pausedWhileOffline = false;

type SocketStore = {
	socket: Socket;
	connected: boolean;
	authenticated: boolean;
	failedToRecover: boolean;
	messages: SocketIOMessage[];
};

type SocketIOMessage = {
	id: string;
	direction: 'emit' | 'receive';
	type: string;
	date: DateTime;
	data?: SocketMessage | { token?: string } | Record<string, unknown>;
};

// SocketEventWatcherStore allows other store to register a function that will be called when a message is received
// It gives other store the ability to self update their state depending on message content.
export type WatcherCallable = (message: SocketMessage<AppEvent>) => void;
function createSocketEventWatcherStore() {
	const { subscribe, update } = writable<Record<string, WatcherCallable | undefined>>({});

	return {
		subscribe,
		register: (key: string, watcher: WatcherCallable) => {
			update((state) => {
				if (state[key]) {
					// Remove the previous watcher if it exists
					delete state[key];
				}
				state[key] = watcher;
				return state;
			});
		},
		unregister: (key: string) => {
			update((state) => {
				delete state[key];
				return state;
			});
		}
	};
}
const socketEventWatcherStore = createSocketEventWatcherStore();

type SocketEventHandler<E extends AppEvent> = (
	data: E extends keyof AppEventPayloadMap ? AppEventPayloadMap[E] : Record<string, unknown>,
	message: SocketMessage<E>
) => void;

/**
 * Register a strongly typed listener for one or more Socket.IO events.
 * Returns an unsubscribe function.
 */
export function onSocketEvent<E extends AppEvent>(
	event: E | E[],
	handler: SocketEventHandler<E>
): () => void {
	const events: AppEvent[] = Array.isArray(event) ? event : [event];
	const key = generateUUID();

	socketEventWatcherStore.register(key, (rawMessage) => {
		const msg = rawMessage as SocketMessage<E>;
		if (events.includes(msg.event)) {
			handler(
				msg.data as E extends keyof AppEventPayloadMap
					? AppEventPayloadMap[E]
					: Record<string, unknown>,
				msg
			);
		}
	});

	return () => {
		socketEventWatcherStore.unregister(key);
	};
}

const SOCKET_TOKEN_TTL_MS = 14 * 60 * 1000;
let socketToken: { value: string; createdAt: number } | null = null;

export function setNextSocketToken(token: string): void {
	socketToken = { value: token, createdAt: Date.now() };
}

async function resolveSocketToken(): Promise<string> {
	if (socketToken && Date.now() - socketToken.createdAt < SOCKET_TOKEN_TTL_MS) {
		return socketToken.value;
	}
	const { token } = await apiSocketToken();
	if (token) {
		setNextSocketToken(token);
	}
	return token;
}

function createSocketStore() {
	const socket = io(url, {
		autoConnect: false,
		auth: async (cb) => {
			try {
				const token = await resolveSocketToken();
				if (!token) {
					isConnecting = false;
					console.error("Can't connect to socket. Failed to get token.");
					socket.disconnect();
					return;
				}

				const message: SocketIOMessage = {
					id: generateUUID(),
					direction: 'emit',
					type: 'connect',
					date: get(now),
					data: { token }
				};

				update((state) => ({
					...state,
					messages: [...state.messages.slice(-numberMessagesToKeep), message]
				}));

				cb({ token });
			} catch (error) {
				isConnecting = false;
				if (!browser || navigator.onLine) {
					console.error("Can't connect to socket. Failed to get token.", error);
				}
				socket.disconnect();
			}
		}
	});

	const { subscribe, update } = writable<SocketStore>({
		socket,
		connected: false,
		authenticated: false,
		failedToRecover: false,
		messages: []
	});

	const pauseForOffline = () => {
		pausedWhileOffline = true;
		isConnecting = false;
		socket.disconnect();
	};

	socket.io.on('error', (error) => {
		isConnecting = false;
		if (browser && !navigator.onLine) {
			pauseForOffline();
			return;
		}
		console.warn('Socket transport temporary error:', error);
	});

	let consecutiveTokenErrors = 0;

	socket.on('connect_error', (error) => {
		isConnecting = false;
		socketToken = null;
		if (browser && !navigator.onLine) {
			pauseForOffline();
			return;
		}

		if (error.message === 'Token verification failed.') {
			consecutiveTokenErrors++;
			if (consecutiveTokenErrors === 1) {
				console.warn(
					'Socket token expired or rejected, refreshing on next reconnect attempt:',
					error.message
				);
				return;
			}
			console.error('Socket connect_error:', error);
			return;
		}

		if (
			error.message &&
			!['websocket error', 'xhr poll error', 'xhr post error', 'timeout'].includes(
				error.message
			)
		) {
			console.error('Socket connect_error:', error);
		} else {
			console.warn('Socket reconnecting after transport interruption:', error.message);
		}
	});

	socket.io.on('reconnect_failed', () => {
		console.error('Socket failed to reconnect after maximum attempts.');
	});

	socket.io.on('reconnect', (attempt) => {
		if (debug) console.log(`Successfully reconnected to socket (attempt ${attempt})`);
	});

	socket.on('connect', () => {
		consecutiveTokenErrors = 0;
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'connect',
			date: get(now)
		};

		if (hasBeenConnected && !socket.recovered) {
			refreshAppData();
		}

		hasBeenConnected = true;
		isConnecting = false;

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			connected: true
		}));
	});

	socket.on('disconnect', (reason) => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'disconnect',
			date: get(now)
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			connected: false,
			authenticated: false
		}));

		if (debug) console.log(`Disconnected from socket (reason: ${reason})`);
	});

	socket.on('message', (data) => {
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'message',
			date: get(now),
			data
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message]
		}));

		// Notify all watchers registered of the incoming message
		Object.values(get(socketEventWatcherStore)).forEach((watcher) => {
			watcher?.(data as SocketMessage<AppEvent>);
		});
	});

	socket.on('connected', () => {
		if (debug) console.log('Connected to socket');

		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'receive',
			type: 'connected',
			date: get(now)
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message],
			authenticated: true
		}));
	});

	const connect = async () => {
		if (browser && !navigator.onLine) {
			pausedWhileOffline = true;
			return;
		}

		// Check connection status before connecting
		if (socket.connected || isConnecting) {
			if (debug) console.log('Already connected, or connecting to socket');
			return;
		}

		isConnecting = true;
		socket.connect();
	};

	const disconnect = () => {
		pausedWhileOffline = false;
		socketToken = null;
		consecutiveTokenErrors = 0;
		const message: SocketIOMessage = {
			id: generateUUID(),
			direction: 'emit',
			date: get(now),
			type: 'disconnect'
		};

		update((state) => ({
			...state,
			messages: [...state.messages.slice(-numberMessagesToKeep), message]
		}));

		socket.disconnect();
	};

	if (browser) {
		window.addEventListener('offline', () => {
			if (socket.connected || isConnecting || hasBeenConnected) {
				pauseForOffline();
			}
		});

		window.addEventListener('online', () => {
			if (pausedWhileOffline) {
				pausedWhileOffline = false;
				void connect();
			}
		});
	}

	return {
		subscribe,
		reconnect: () => {
			if (debug) console.log('Reconnecting to web socket');
			isConnecting = false;
			socketToken = null;
			if (socket.connected) {
				socket.disconnect();
			}

			void connect();
		},
		connect,
		disconnect
	};
}

export const socketStore = createSocketStore();
